import { z } from 'zod';
import { LocationGatewayDiscoverySettingsSchema, LocationGatewayForwardingTargetSchema, LocationGatewayProjectXmlSettingsSchema, LocationGatewayTagBlinkFrequencyHzSchema, LocationGatewayAlgorithmParamsXmlSchema, } from './schemas.js';
import { LocationCloudTelemetrySettingsSchema, LocationOrchestratorSettingsSchema, } from './location-telemetry-settings.schema.js';
import { mergeValidatedComponent } from './gateway-config-component-merge.js';
/** S3 override / audit filenames for Woxu LS-1000 + orchestrator + cloud ingest. */
export const LOCATION_GATEWAY_OVERRIDE_FILENAMES = [
    'ls1000-sockets.json',
    'ls1000-forwarding.json',
    'ls1000-anchor-discovery.json',
    'ls1000-tag-config.json',
    'ls1000-positioning.json',
    'orchestrator.json',
    'cloud-ingest.json',
];
export const Ls1000SocketsSettingsSchema = z.object({
    engineHost: z.string().trim().min(1),
    samplingTransferPort: z.number().int().min(1).max(65535),
    controlPort: z.number().int().min(1).max(65535),
});
export const Ls1000ForwardingSettingsSchema = z.object({
    locationGatewayForwardingTargets: z.array(LocationGatewayForwardingTargetSchema).min(1),
});
export const Ls1000AnchorDiscoverySettingsSchema = z.object({
    discovery: LocationGatewayDiscoverySettingsSchema,
});
export const Ls1000TagConfigSettingsSchema = z.object({
    tagBlinkFrequencyHz: LocationGatewayTagBlinkFrequencyHzSchema,
});
export const Ls1000PositioningSettingsSchema = z.object({
    positioningMode: z.enum(['tof', 'tdoa']),
    tofAlgorithmParamsXml: LocationGatewayAlgorithmParamsXmlSchema,
    toaAlgorithmParamsXml: LocationGatewayAlgorithmParamsXmlSchema,
});
export const LocationGatewayEffectiveConfigSchema = z.object({
    'ls1000-sockets.json': Ls1000SocketsSettingsSchema,
    'ls1000-forwarding.json': Ls1000ForwardingSettingsSchema,
    'ls1000-anchor-discovery.json': Ls1000AnchorDiscoverySettingsSchema,
    'ls1000-tag-config.json': Ls1000TagConfigSettingsSchema,
    'ls1000-positioning.json': Ls1000PositioningSettingsSchema,
    'orchestrator.json': LocationOrchestratorSettingsSchema,
    'cloud-ingest.json': LocationCloudTelemetrySettingsSchema,
});
export const LocationGatewayOverridesPartialSchema = z.object({
    'ls1000-sockets.json': Ls1000SocketsSettingsSchema.partial().optional(),
    'ls1000-forwarding.json': Ls1000ForwardingSettingsSchema.partial().optional(),
    'ls1000-anchor-discovery.json': Ls1000AnchorDiscoverySettingsSchema.deepPartial().optional(),
    'ls1000-tag-config.json': Ls1000TagConfigSettingsSchema.partial().optional(),
    'ls1000-positioning.json': Ls1000PositioningSettingsSchema.partial().optional(),
    'orchestrator.json': LocationOrchestratorSettingsSchema.partial().optional(),
    'cloud-ingest.json': LocationCloudTelemetrySettingsSchema.partial().optional(),
});
const defaultProjectXml = LocationGatewayProjectXmlSettingsSchema.parse({});
export const WOXU_LS1000_SYSTEM_TEMPLATE = {
    'ls1000-sockets.json': {
        engineHost: defaultProjectXml.engineHost,
        samplingTransferPort: defaultProjectXml.samplingTransferPort,
        controlPort: defaultProjectXml.controlPort,
    },
    'ls1000-forwarding.json': {
        locationGatewayForwardingTargets: defaultProjectXml.locationGatewayForwardingTargets,
    },
    'ls1000-anchor-discovery.json': {
        discovery: defaultProjectXml.discovery,
    },
    'ls1000-tag-config.json': {
        tagBlinkFrequencyHz: defaultProjectXml.tagBlinkFrequencyHz,
    },
    'ls1000-positioning.json': {
        positioningMode: defaultProjectXml.positioningMode,
        tofAlgorithmParamsXml: defaultProjectXml.tofAlgorithmParamsXml,
        toaAlgorithmParamsXml: defaultProjectXml.toaAlgorithmParamsXml,
    },
    'orchestrator.json': LocationOrchestratorSettingsSchema.parse({}),
    'cloud-ingest.json': LocationCloudTelemetrySettingsSchema.parse({}),
};
export function assembleLs1000ProjectSettings(effective) {
    return LocationGatewayProjectXmlSettingsSchema.parse({
        ...effective['ls1000-sockets.json'],
        discovery: effective['ls1000-anchor-discovery.json'].discovery,
        locationGatewayForwardingTargets: effective['ls1000-forwarding.json'].locationGatewayForwardingTargets,
        tagBlinkFrequencyHz: effective['ls1000-tag-config.json'].tagBlinkFrequencyHz,
        ...effective['ls1000-positioning.json'],
    });
}
export function mergeLocationGatewayEffectiveConfig(template, tenantPartials, gatewayPartials, pushTimePartials) {
    return {
        'ls1000-sockets.json': mergeValidatedComponent(Ls1000SocketsSettingsSchema, template['ls1000-sockets.json'], tenantPartials['ls1000-sockets.json'], gatewayPartials['ls1000-sockets.json'], pushTimePartials?.['ls1000-sockets.json']),
        'ls1000-forwarding.json': mergeValidatedComponent(Ls1000ForwardingSettingsSchema, template['ls1000-forwarding.json'], tenantPartials['ls1000-forwarding.json'], gatewayPartials['ls1000-forwarding.json'], pushTimePartials?.['ls1000-forwarding.json']),
        'ls1000-anchor-discovery.json': mergeValidatedComponent(Ls1000AnchorDiscoverySettingsSchema, template['ls1000-anchor-discovery.json'], tenantPartials['ls1000-anchor-discovery.json'], gatewayPartials['ls1000-anchor-discovery.json'], pushTimePartials?.['ls1000-anchor-discovery.json']),
        'ls1000-tag-config.json': mergeValidatedComponent(Ls1000TagConfigSettingsSchema, template['ls1000-tag-config.json'], tenantPartials['ls1000-tag-config.json'], gatewayPartials['ls1000-tag-config.json'], pushTimePartials?.['ls1000-tag-config.json']),
        'ls1000-positioning.json': mergeValidatedComponent(Ls1000PositioningSettingsSchema, template['ls1000-positioning.json'], tenantPartials['ls1000-positioning.json'], gatewayPartials['ls1000-positioning.json'], pushTimePartials?.['ls1000-positioning.json']),
        'orchestrator.json': mergeValidatedComponent(LocationOrchestratorSettingsSchema, template['orchestrator.json'], tenantPartials['orchestrator.json'], gatewayPartials['orchestrator.json'], pushTimePartials?.['orchestrator.json']),
        'cloud-ingest.json': mergeValidatedComponent(LocationCloudTelemetrySettingsSchema, template['cloud-ingest.json'], tenantPartials['cloud-ingest.json'], gatewayPartials['cloud-ingest.json'], pushTimePartials?.['cloud-ingest.json']),
    };
}
export const LocationGatewayConfigPreviewResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        gatewaySerialNumber: z.string(),
        siteId: z.string().nullable(),
        locationAdapterKey: z.string().optional(),
        effective: LocationGatewayEffectiveConfigSchema,
        assembled: z.object({
            projectXmlSettings: LocationGatewayProjectXmlSettingsSchema,
            orchestratorSettings: LocationOrchestratorSettingsSchema,
            cloudIngestSettings: LocationCloudTelemetrySettingsSchema,
        }),
    }),
});
export const LocationGatewayOverridesPutBodySchema = z.object({
    overrides: LocationGatewayOverridesPartialSchema,
});
export const LocationGatewayOverridesResponseSchema = z.object({
    success: z.literal(true),
    data: LocationGatewayOverridesPartialSchema,
});
export const LocationGatewaySerialParamsSchema = z.object({
    serialNumber: z.string().min(1).max(128),
});
//# sourceMappingURL=location-gateway-overrides.schema.js.map